import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, setAuthToken } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { MailCheck, MailWarning, Loader2 } from "lucide-react";
import { readProjectQuizDraft, PROJECT_QUIZ_STORAGE_KEY } from "../lib/projectQuiz";
import { applyFounderQuizDraft } from "../lib/founderQuizDraft";
import { convertQuizCompletion } from "../components/quiz/ShareScore";
import { applyFunderQuizMandate } from "./Signup";

// Handles the link from the verification email: POSTs the token, auto-logs
// the founder in, replays any pre-signup quiz drafts, then routes them to
// the right first page (onboarding / project intake / funder terminal).
export default function VerifyEmail() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh, setUser } = useAuth();
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [errMsg, setErrMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    try { sessionStorage.removeItem("clinq_david_popup_seen"); } catch (_) { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (data?.token) setAuthToken(data.token);
        setUser({ user_id: data.user_id, email: data.email, name: data.name, role: data.role });
        setState("success");
        // Replay pre-signup drafts (same browser only) before routing.
        let dest = "/onboarding";
        if (data.role === "funder") {
          await applyFunderQuizMandate(api);
          dest = "/funder/terminal";
        } else if (data.role === "admin") {
          dest = "/admin/dashboard";
        } else if (data.role === "studio") {
          dest = "/studio/dashboard";
        } else {
          const pendingRole = localStorage.getItem("clinq_pending_signup_role");
          const draft = readProjectQuizDraft();
          const wantsProject = pendingRole === "project" || !!(draft && draft.completed_at);
          if (wantsProject) {
            try {
              await api.post("/project/bootstrap", { quiz: draft?.completed_at ? draft : {} });
              try { localStorage.removeItem(PROJECT_QUIZ_STORAGE_KEY); } catch (_) { /* ignore */ }
              if (draft?.completed_at) convertQuizCompletion(api);
              dest = "/project/intake";
            } catch (_) { dest = "/onboarding"; }
          } else {
            await applyFounderQuizDraft(api);
          }
          try { localStorage.removeItem("clinq_pending_signup_role"); } catch (_) { /* ignore */ }
        }
        await refresh();
        setTimeout(() => nav(dest), 1200);
      } catch (e) {
        const d = e.response?.data?.detail;
        setErrMsg(typeof d === "string" ? d : "This verification link is invalid or has expired.");
        setState("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    setResendState("sending");
    try {
      await api.post("/auth/resend-verification", { email: resendEmail.trim() });
      setResendState("sent");
    } catch (_) {
      setResendState("idle");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F8F8]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mx-auto max-w-md px-4 py-16 sm:px-6 sm:py-24">
        <Link to="/" className="text-2xl font-extrabold text-[#0A8B85]" data-testid="verify-email-logo">Clinq</Link>

        {state === "verifying" && (
          <div className="mt-10 rounded-2xl border border-neutral-200 bg-white p-8 text-center" data-testid="verify-email-loading">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#0A8B85]" />
            <h1 className="mt-5 text-xl font-bold text-neutral-900">Verifying your email…</h1>
            <p className="mt-2 text-sm text-neutral-600">Hang tight — this only takes a second.</p>
          </div>
        )}

        {state === "success" && (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-white p-8 text-center" data-testid="verify-email-success">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <MailCheck className="h-7 w-7 text-emerald-600" />
            </div>
            <h1 className="mt-5 text-xl font-bold text-neutral-900">Email verified!</h1>
            <p className="mt-2 text-sm text-neutral-600">You&apos;re all set. Signing you in and taking you to your dashboard…</p>
            <div className="mt-4 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#0A8B85]" />
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="mt-10 rounded-2xl border border-amber-200 bg-white p-8" data-testid="verify-email-error">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
              <MailWarning className="h-7 w-7 text-amber-600" />
            </div>
            <h1 className="mt-5 text-center text-xl font-bold text-neutral-900">Link expired or invalid</h1>
            <p className="mt-2 text-center text-sm text-neutral-600" data-testid="verify-email-error-msg">{errMsg}</p>

            {resendState === "sent" ? (
              <p className="mt-6 rounded-lg bg-emerald-50 px-4 py-3 text-center text-sm font-semibold text-emerald-700" data-testid="verify-email-resend-sent">
                A fresh verification email is on its way. Check your inbox (and spam folder).
              </p>
            ) : (
              <form onSubmit={resend} className="mt-6 space-y-3">
                <input data-testid="verify-email-resend-input" type="email" required placeholder="Your signup email"
                  value={resendEmail} onChange={(e) => setResendEmail(e.target.value)}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#0A8B85]" />
                <button data-testid="verify-email-resend-btn" type="submit" disabled={resendState === "sending"}
                  className="w-full rounded-lg bg-[#0A8B85] py-3 text-sm font-semibold text-white hover:bg-[#066667] disabled:opacity-50">
                  {resendState === "sending" ? "Sending…" : "Send a new verification email"}
                </button>
              </form>
            )}

            <p className="mt-6 text-center text-sm text-neutral-600">
              Already verified?{" "}
              <Link to="/login" className="font-semibold text-[#0A8B85] hover:underline" data-testid="verify-email-to-login">Log in</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
